import { useTranslation } from 'react-i18next';
import { customRound, displayQuarter, europeanFormatNumber, eurosPerHa } from '@/helpers';
import { useLandRight } from '@/hooks';
import { Button, Icon, Typography } from '@/components';
import { useDUPStatus } from '@/features/projects/hooks';

export type LandRightsBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
  readonly isDataCenterTechnology: boolean;
};

export function LandRightsBox({ normalizedProject, isEditable, onEdit, isDataCenterTechnology }: LandRightsBoxProps) {
  const { t } = useTranslation(['projects']);
  const land = normalizedProject.landRights?.[0];

  const { map: landRightsmap } = useLandRight();
  const { options: statusOptions } = useDUPStatus();
  const [statusSelected] = statusOptions.filter((status) => status.value === land?.dup_status);

  if (!isEditable && land === undefined) return null;

  const roundedLandSurface = customRound(land?.land_surface);

  const allFields = [
    { label: t('Land Surface'), value: `${europeanFormatNumber(roundedLandSurface)} ha` },
    { label: t('Land Types'), value: land?.land_types?.map(landRightsmap).join(' + ') },
    { label: t('Land Price'), value: eurosPerHa(land?.land_price) },
    { label: t('Land Contracted Percentage'), value: `${europeanFormatNumber(land?.land_contracted_percentage)} %` },
    { label: t('Land Contract Duration'), value: land?.land_contract_duration },
    {
      label: t('Land Comments'),
      value: <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">{land?.land_comments}</span>,
    },
    { label: t('Expropriation Status'), value: statusSelected?.label ?? land?.dup_status },
    { label: t('Expropriation Expected Date'), value: displayQuarter(land?.dup_date) },
    {
      label: t('Expropriation Comments'),
      value: <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">{land?.dup_comments}</span>,
    },
  ];

  isDataCenterTechnology &&
    allFields.push({ label: t('Gross Built Area'), value: `${europeanFormatNumber(land?.land_gross_built_area)} m²` });

  const visibleFields = isEditable
    ? allFields
    : allFields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  if (!isEditable && visibleFields.length === 0) return null;

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="tree" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Land Rights')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
